import { useEffect, useState } from "react";
import Toaster, { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";
import { formatDate } from "../../helper/dateFormatter";
import { FilterCriteria } from "tasks-experience-client";
import {
  CellClickedEvent,
  ColDef,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { GenericGrid } from "../../grid/GenericGrid";

import { createCarePlanApi } from "../../api-helpers/PatientApiHelper";
import { UserContextHolder } from "../../auth/UserContextHolder";
import { CarePlanHeader } from "./CarePlanHeader";

import { ReactComponent as MenuIcon } from "../../assets/img/grid-menu.svg";
import { ReactComponent as HistoryIcon } from "../../assets/img/history.svg";
import { Modal } from "react-bootstrap";
import { CarePlanHistory } from "./CarePlanHistory";
import ViewCarePlan from "./ViewCarePlan";
import {
  CarePlanDto,
  GridDataRequest,
  GridDataResponse,
} from "patients-experience-client";
import { workflowStatesCache } from "../../grid/renderers/WorkflowStateCellValueGetter";
import { capitalizeInitials } from "../../helper/TextUtils";
import { ColoredTestRenderer } from "../../renderers/ColoredTestRenderer";
import { formatDateTimeToLocal } from "../schedule/dateFormat";
import { createGridApi } from "../../api-helpers/GridApiHelper";

export interface Props {
  patientId?: string;
  breadcrumbs?: boolean | undefined;
}

export const getWorkflowId = (stateName: string): string => {
  const workflowState = Array.from(workflowStatesCache.entries()).find(
    ([key, value]) => value === stateName,
  )?.[0];
  return workflowState || "";
};

export const CarePlanGrid = ({ patientId, breadcrumbs }: Props) => {
  const [currentUserId, setCurrentUserId] = useState<string>();
  const [filters, setFilters] = useState<FilterCriteria[]>([]);
  const [initialFiltersReady, setInitialFiltersReady] = useState(false);
  const [gridReloadKey, setGridReloadKey] = useState(0);

  const [modalShow, setModalShow] = useState(false);
  const [historyModalShow, setHistoryModalShow] = useState(false);
  const [modalSubTitle, setModalSubTitle] = useState("");
  const [selectedCarePlan, setSelectedCarePlan] = useState({} as CarePlanDto);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
  });

  const carePlanApi = createCarePlanApi();
  const gridApi = createGridApi();
  useEffect(() => {
    UserContextHolder.addListener(() => {
      setCurrentUserId(UserContextHolder.getUser()?.id);
    });
  }, []);

  const reloadGrid = () => {
    setGridReloadKey(gridReloadKey + 1);
  };

  useEffect(() => {
    if (initialFiltersReady) {
      return;
    }

    if (!currentUserId) {
      return;
    }

    setInitialFiltersReady(true);
  }, [filters]);

  const handleViewUpdateCarePlan = async (
    params: CellClickedEvent | ValueGetterParams,
  ) => {
    const carePlan = params.data;
    // Get workflow state from workflowStatesCache whose value is equal to "Review Pending"

    const reviewPendingStateId = getWorkflowId("Review Pending");
    const completedStateId = getWorkflowId("Completed");
    const rejectedStateId = getWorkflowId("Rejected");

    console.log(reviewPendingStateId);

    if (carePlan?.isDraft) {
      setModalSubTitle(
        `Generated on : ${formatDateTimeToLocal(carePlan?.createdAt?.toLocaleString())}`,
      );
    } else if (carePlan.designerStateId === completedStateId) {
      setModalSubTitle(
        `Reviewed on : ${formatDateTimeToLocal(carePlan?.reviewedAt?.toLocaleString() || "")}`,
      );
    } else if (carePlan.designerStateId === rejectedStateId) {
      setModalSubTitle(
        `Rejected on : ${formatDateTimeToLocal(carePlan?.reviewedAt?.toLocaleString() || "")}`,
      );
    } else {
      setModalSubTitle(``);
    }
    setSelectedCarePlan(carePlan);
    setModalShow(!modalShow);
    await loadCarePlanDetails(carePlan.id);
  };

  const ViewIcon = (event: CellClickedEvent | ValueGetterParams) => {
    return (
      <div className="d-flex gap-8 align-items-center justify-content-center h-100 cursor-pointer">
        <div
          onClick={() => {
            setSelectedCarePlan(event.data);
            setHistoryModalShow(true);
          }}
        >
          <HistoryIcon />
        </div>

        <div onClick={() => handleViewUpdateCarePlan(event)}>
          <MenuIcon />
        </div>
      </div>
      // <div role="button" >

      // </div>
    );
  };

  // const DisplayIcon = () => {
  //   return (
  //     <div className="d-flex align-items-center justify-content-center h-100 cursor-pointer">
  //       <HistoryIcon />
  //     </div>
  //   );
  // };

  const openInNewTab = (url: string) => {
    const newWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  };

  const [colDefs, setColDefs] = useState<ColDef[]>([
    {
      field: "createdAtMonth",
      minWidth: 100,
      maxWidth: 120,
      headerName: "Month",
      pinned: "left",
      sortable: true,
      valueFormatter: function (params: ValueFormatterParams) {
        return new Date(params.data.createdAt).toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      field: "patientName",
      headerName: "Patient",
      minWidth: 150,
      width: 200,
      maxWidth: 300,
      pinned: "left",
      hide: patientId != undefined,
      sortable: true,
      cellRenderer: (params: ValueGetterParams) => {
        return (
          <a
            href="#"
            className="a-link a-underline text-secondary"
            onClick={() => {
              openInNewTab(`/patients/${params.data["patientId"]}/summary`);
            }}
          >
            {capitalizeInitials(params.data.patientName)}
          </a>
        );
      },
    },
    {
      field: "createdAt",
      minWidth: 120,
      width: 140,
      maxWidth: 200,
      headerName: "Created On",
      sort: "desc",
      sortable: true,
      valueFormatter: function (params) {
        return formatDate(params.data.createdAt);
      },
    },
    {
      field: "createdByName",
      minWidth: 150,
      maxWidth: 340,
      headerName: "Created By",
      sortable: true,
    },
    {
      field: "reviewedAt",
      minWidth: 120,
      width: 120,
      maxWidth: 200,
      headerName: "Reviewed On",
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        return formatDate(params.data.reviewedAt);
      },
    },
    {
      field: "reviewedByName",
      minWidth: 150,
      maxWidth: 340,
      headerName: "Reviewed By",
      sortable: true,
    },
    {
      field: "stateName",
      cellRenderer: ColoredTestRenderer,
      headerName: "Status",
      sortable: true,
      minWidth: 150,
      width: 180,
      maxWidth: 300,
    },
    {
      cellRenderer: ViewIcon,
      minWidth: 120,
      maxWidth: 150,
      pinned: "right",
      sortable: false,
    },
  ]);

  const loadCarePlanDetails = async (id: string) => {
    try {
      const response =
        await carePlanApi.carePlanControllerGetCarePlanDetails(id);
      setSelectedCarePlan(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  const loadData = async (
    request: GridDataRequest,
  ): Promise<GridDataResponse> => {
    const response = await gridApi.gridControllerGetCarePlanGridData(request);
    return response.data;
  };

  return (
    <>
      <div className="row">
        <Toaster
          show={showToast}
          onClose={() => setShowToast(false)}
          type={toastConfig.type}
          title={toastConfig.title}
          message={toastConfig.message}
          icon={toastConfig.icon}
        />
      </div>
      {currentUserId && (
        <main className="main">
          <CarePlanHeader
            breadcrumbs={breadcrumbs}
            patientId={patientId}
            currentUserId={currentUserId}
            onUpdateFilters={setFilters}
          />
          <div className="d-flex px-3 px-md-4 px-lg-5 d-flex align-items-stretch">
            <div className="flex-fill">
              <div className="py-4">
                {initialFiltersReady && (
                  <GenericGrid
                    gridId="care-plan-grid"
                    colDefs={colDefs}
                    dataProvider={loadData}
                    filters={filters}
                    key={gridReloadKey}
                  ></GenericGrid>
                )}
              </div>
            </div>
          </div>
        </main>
      )}

      <Modal
        show={historyModalShow}
        onHide={() => setHistoryModalShow(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <>
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              History
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <CarePlanHistory selectedCarePlan={selectedCarePlan!} />
          </Modal.Body>
        </>
      </Modal>
      <Modal
        size={"xl"}
        show={modalShow}
        onHide={() => setModalShow(false)}
        aria-labelledby="containedCarePlan"
        centered
      >
        <>
          <Modal.Header className="px-8" closeButton>
            <Modal.Title id="containedCarePlan">
              <div>
                {/* {selectedCarePlan.isDraft  && "View Care Plan"}  */}
                {selectedCarePlan.status === "Review Pending"
                  ? "Review Care Plan"
                  : "View Care Plan"}
              </div>
              <div className="fs-12 fw-normal">{modalSubTitle}</div>
            </Modal.Title>
          </Modal.Header>
          <ViewCarePlan
            onHide={() => setModalShow(false)}
            selectedCarePlan={selectedCarePlan}
            reloadGrid={reloadGrid}
            reloadCarePlan={loadCarePlanDetails}
          />
        </>
      </Modal>
    </>
  );
};
